import { createFile, DataStream, Endianness } from 'mp4box';
import { FRAME_CACHE_SIZE } from '../constants';

/* eslint-disable @typescript-eslint/no-explicit-any */

interface MP4VideoTrack {
  id: number;
  codec: string;
  track_width: number;
  track_height: number;
}

interface MP4Info {
  videoTracks: MP4VideoTrack[];
}

interface MP4Sample {
  cts: number;
  timescale: number;
  duration: number;
  is_sync: boolean;
  data: ArrayBuffer;
}

/**
 * Frame cache entry — stores a decoded VideoFrame keyed by its timestamp in µs.
 * We track insertion order for LRU eviction.
 */
interface CacheEntry {
  frame: VideoFrame;
  insertOrder: number;
}

/**
 * 2026 Gold Standard FrameDecoder
 *
 * Uses a look-ahead frame cache so the render loop can request timestamps
 * that are already decoded. GOP-aware: on cache miss, rewinds to the
 * nearest keyframe and decodes forward, populating the cache as it goes.
 *
 * Memory management: frames are explicitly closed on eviction or dispose.
 */
export class FrameDecoder {
  private mp4: any = null;
  private decoder: VideoDecoder | null = null;
  private trackId = 0;
  private samples: MP4Sample[] = [];
  private codecConfig: VideoDecoderConfig | null = null;
  private ready = false;

  // Frame cache: timestamp (µs) → CacheEntry
  private frameCache = new Map<number, CacheEntry>();
  private insertCounter = 0;

  // Decode-in-progress lock — prevents concurrent decode runs
  private decoding = false;
  // Queue: when a decode is in progress, the next requested range is queued
  private pendingDecode: { startIdx: number; endIdx: number } | null = null;

  // Frames emitted by the decoder that haven't been cached yet
  private decodedQueue: VideoFrame[] = [];

  async init(proxyData: ArrayBuffer): Promise<void> {
    this.dispose();

    return new Promise<void>((resolve, reject) => {
      this.mp4 = createFile();

      this.mp4.onReady = (info: MP4Info) => {
        const videoTrack = info.videoTracks[0];
        if (!videoTrack) {
          reject(new Error('No video track found'));
          return;
        }

        this.trackId = videoTrack.id;

        this.codecConfig = {
          codec: videoTrack.codec,
          codedWidth: videoTrack.track_width,
          codedHeight: videoTrack.track_height,
          description: this.getDescription(videoTrack.id),
        };

        this.decoder = new VideoDecoder({
          output: (frame) => {
            this.decodedQueue.push(frame);
          },
          error: (e) => console.error('VideoDecoder error:', e),
        });

        this.decoder.configure(this.codecConfig);

        this.mp4.setExtractionOptions(this.trackId, null, {
          nbSamples: Infinity,
        });

        // start() triggers sample extraction — without it, onSamples never fires
        this.mp4.start();

        this.ready = true;
        resolve();
      };

      this.mp4.onError = (e: string) => reject(new Error(e));

      this.mp4.onSamples = (_id: number, _user: unknown, samples: MP4Sample[]) => {
        this.samples.push(...samples);
        console.log('[Decoder] onSamples:', samples.length, 'total:', this.samples.length);
      };

      // Feed the data — mp4box needs a buffer with fileStart property
      const buf: any = proxyData.slice(0);
      buf.fileStart = 0;
      this.mp4.appendBuffer(buf);
      this.mp4.flush();
    });
  }

  private getDescription(trackId: number): Uint8Array | undefined {
    if (!this.mp4) return undefined;
    const trak = this.mp4.getTrackById(trackId);
    if (!trak) return undefined;

    const stsd = trak?.mdia?.minf?.stbl?.stsd;
    if (!stsd?.entries?.length) return undefined;
    const entry = stsd.entries[0];
    const avcC = entry.avcC || entry.hvcC;
    if (!avcC) return undefined;

    const stream = new DataStream(undefined, 0, Endianness.BIG_ENDIAN);
    avcC.write(stream);
    return new Uint8Array(stream.buffer as ArrayBuffer, 8);
  }

  /** Convert sample to its timestamp in µs */
  private sampleTimeUs(sample: MP4Sample): number {
    return Math.round((sample.cts / sample.timescale) * 1_000_000);
  }

  /** Find the sample index closest to (but not exceeding) the given time in µs */
  private findSampleIndex(timeUs: number): number {
    let best = 0;
    for (let i = 0; i < this.samples.length; i++) {
      if (this.sampleTimeUs(this.samples[i]) <= timeUs) {
        best = i;
      } else {
        break;
      }
    }
    return best;
  }

  /** Find the nearest keyframe at or before the given sample index */
  private findKeyframeBefore(idx: number): number {
    for (let i = idx; i >= 0; i--) {
      if (this.samples[i].is_sync) return i;
    }
    return 0;
  }

  /** Evict oldest frames from cache when it exceeds FRAME_CACHE_SIZE */
  private evictCache() {
    while (this.frameCache.size > FRAME_CACHE_SIZE) {
      let oldestKey: number | null = null;
      let oldestOrder = Infinity;
      for (const [key, entry] of this.frameCache) {
        if (entry.insertOrder < oldestOrder) {
          oldestOrder = entry.insertOrder;
          oldestKey = key;
        }
      }
      if (oldestKey !== null) {
        const entry = this.frameCache.get(oldestKey)!;
        entry.frame.close();
        this.frameCache.delete(oldestKey);
      }
    }
  }

  /**
   * Decode samples from startIdx to endIdx (inclusive) and cache the resulting frames.
   * Performs a full reset+reconfigure to ensure clean decoder state.
   */
  private async decodeRange(startIdx: number, endIdx: number): Promise<void> {
    if (!this.decoder || !this.codecConfig) return;

    // Find the keyframe we need to start from
    const keyIdx = this.findKeyframeBefore(startIdx);

    // Reset decoder for a fresh decode run
    await this.decoder.flush();
    this.decoder.reset();
    this.decoder.configure(this.codecConfig);
    this.decodedQueue = [];

    // Feed all samples from keyframe through endIdx
    for (let i = keyIdx; i <= endIdx; i++) {
      const sample = this.samples[i];
      const chunk = new EncodedVideoChunk({
        type: sample.is_sync ? 'key' : 'delta',
        timestamp: this.sampleTimeUs(sample),
        duration: Math.round((sample.duration / sample.timescale) * 1_000_000),
        data: sample.data,
      });
      this.decoder.decode(chunk);
    }

    await this.decoder.flush();

    // Cache all decoded frames
    for (const frame of this.decodedQueue) {
      const ts = frame.timestamp;
      // If we already have this timestamp cached, close the old one
      const existing = this.frameCache.get(ts);
      if (existing) {
        existing.frame.close();
      }
      this.frameCache.set(ts, { frame, insertOrder: this.insertCounter++ });
    }
    this.decodedQueue = [];

    this.evictCache();
  }

  /**
   * Get a frame for the given time in ms.
   * Returns immediately from cache if available, otherwise decodes on demand.
   */
  async getFrame(timeMs: number): Promise<VideoFrame | null> {
    if (!this.ready || !this.decoder || this.samples.length === 0) {
      return null;
    }

    const timeUs = timeMs * 1000;
    const targetIdx = this.findSampleIndex(timeUs);
    const targetTs = this.sampleTimeUs(this.samples[targetIdx]);

    // Cache hit — return the frame directly
    const cached = this.frameCache.get(targetTs);
    if (cached) {
      // Bump insert order for LRU
      cached.insertOrder = this.insertCounter++;
      return cached.frame;
    }

    // Cache miss — decode from keyframe through target
    if (this.decoding) {
      // Another decode is in progress; queue this request
      this.pendingDecode = { startIdx: targetIdx, endIdx: targetIdx };
      return null;
    }

    this.decoding = true;
    try {
      await this.decodeRange(targetIdx, targetIdx);
    } finally {
      this.decoding = false;
    }

    // Process any queued decode
    if (this.pendingDecode) {
      const pending = this.pendingDecode;
      this.pendingDecode = null;
      // Fire and forget — will be picked up on next getFrame call
      this.prefetchRange(pending.startIdx, Math.min(pending.endIdx, this.samples.length - 1));
    }

    const result = this.frameCache.get(targetTs);
    return result?.frame ?? null;
  }

  /**
   * Pre-fetch frames for the given time range (ms).
   * Called by the render loop to populate the cache ahead of the playhead.
   * Non-blocking — if a decode is already in progress, the request is queued.
   */
  async prefetch(fromMs: number, toMs: number): Promise<void> {
    if (!this.ready || this.samples.length === 0) return;

    const fromUs = fromMs * 1000;
    const toUs = toMs * 1000;
    const startIdx = this.findSampleIndex(fromUs);
    const endIdx = this.findSampleIndex(toUs);

    // Check if the entire range is already cached
    let allCached = true;
    for (let i = startIdx; i <= endIdx; i++) {
      const ts = this.sampleTimeUs(this.samples[i]);
      if (!this.frameCache.has(ts)) {
        allCached = false;
        break;
      }
    }
    if (allCached) return;

    await this.prefetchRange(startIdx, endIdx);
  }

  private async prefetchRange(startIdx: number, endIdx: number): Promise<void> {
    if (this.decoding) {
      this.pendingDecode = { startIdx, endIdx };
      return;
    }

    this.decoding = true;
    try {
      await this.decodeRange(startIdx, endIdx);
    } finally {
      this.decoding = false;
    }
  }

  /**
   * Hard seek — clears the cache and decodes fresh from the target time.
   * Use when the user drags the playhead to a distant position.
   */
  async hardSeek(timeMs: number): Promise<VideoFrame | null> {
    this.clearCache();
    return this.getFrame(timeMs);
  }

  /** Check if a frame for the given time (ms) is in the cache */
  hasFrame(timeMs: number): boolean {
    if (this.samples.length === 0) return false;
    const timeUs = timeMs * 1000;
    const targetIdx = this.findSampleIndex(timeUs);
    const targetTs = this.sampleTimeUs(this.samples[targetIdx]);
    return this.frameCache.has(targetTs);
  }

  /** Clear all cached frames */
  private clearCache() {
    for (const [, entry] of this.frameCache) {
      entry.frame.close();
    }
    this.frameCache.clear();
    this.insertCounter = 0;
  }

  /** Legacy API — kept for backward compat, delegates to getFrame */
  async seekToTime(timeMs: number): Promise<VideoFrame> {
    const frame = await this.getFrame(timeMs);
    if (!frame) throw new Error('Failed to decode frame');
    return frame;
  }

  dispose() {
    this.clearCache();
    for (const f of this.decodedQueue) f.close();
    this.decodedQueue = [];
    this.decoder?.close();
    this.decoder = null;
    this.mp4?.flush();
    this.mp4 = null;
    this.samples = [];
    this.ready = false;
    this.codecConfig = null;
    this.decoding = false;
    this.pendingDecode = null;
  }
}
